import clsx from 'clsx';

import { type ReactElement, type ReactNode, cloneElement } from 'react';

import type { CarbonIconProps } from '@carbon/icons-react';

import type { Props as InteractiveGroupProps } from '@/components/InteractiveGroup';
import { InteractiveGroup } from '@/components/InteractiveGroup';

import styles from './content-description.module.scss';

type DescriptionContent =
  | {
      title: string;
      copy?: string | ReactNode;
    }
  | {
      title?: string;
      copy: string | ReactNode;
    };

export type Props = {
  cta?: InteractiveGroupProps;
  titleSize?: 'sm' | 'lg';
  customIcon?: ReactElement<CarbonIconProps>;
} & DescriptionContent;

export function ContentDescription({ title, copy, cta, titleSize = 'lg', customIcon }: Props) {
  const showTitle = !!title;
  const titleClassName = styles[`content-description__title--size-${titleSize}`];
  const ctaHasButton = cta?.primary.as === 'button' || cta?.secondary?.as === 'button';

  const renderIcon = customIcon
    ? cloneElement(customIcon, {
        className: styles['content-description__icon'],
        size: '40',
        height: '40',
        width: '40',
      })
    : null;

  return (
    <div>
      {renderIcon && renderIcon}
      {showTitle && (
        <h3
          className={clsx(
            titleClassName,
            { 'text-heading-04': titleSize === 'lg' },
            { 'text-heading-03': titleSize === 'sm' }
          )}
        >
          {title}
        </h3>
      )}
      {copy && (
        <div
          className={clsx(styles['content-description__copy'], {
            [styles['content-description__copy--big-spacing']]: ctaHasButton,
          })}
        >
          {copy}
        </div>
      )}
      {cta && <InteractiveGroup {...cta} />}
    </div>
  );
}
